/**
 * RitualSidebar Component
 * Right column containing action card, community/participant activity,
 * organizer section, and coach widget
 */

import { useQueryClient } from "@tanstack/react-query"
import {
  Calendar,
  Check,
  EllipsisVertical,
  Link2,
  MapPin,
  MessageCircle,
  Monitor,
  MoreVertical,
  RefreshCw,
  Sparkles,
  Trash2,
  Video,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { GuestAction } from "@/components/guest"
import {
  HeartFilledIcon,
  HeartOutlineIcon,
  InviteAcceptedIcon,
  InviteDeclinedIcon,
} from "@/components/icons"
import { LeaveRitualModal } from "@/components/modals/LeaveRitualModal"
import { RefundRequestModal } from "@/components/modals/RefundRequestModal"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { MonthViewCalendar } from "@/components/ui/month-view-calendar"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import {
  useCreateRitualGroupChat,
  useOrganizerChatByRitualId,
} from "@/features/chats/hooks/useRitualGroupChat"
import { startCoachChat } from "@/features/chats/services/ai-chat.service"
import { useCommunityJoinActions } from "@/features/communities/hooks/useCommunityMembership"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { ConnectInstagramPrompt } from "@/features/social-share/components/ConnectInstagramPrompt"
import { InstagramShareDialog } from "@/features/social-share/components/InstagramShareDialog"
import { useInstagramConnection } from "@/features/social-share/hooks/useInstagramConnection"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import {
  useAcceptRitualMemberInvite,
  useInviteRitualMember,
  useResendRitualMemberInvite,
  useRitualMemberInvitees,
  useWithdrawRitualMemberInvite,
} from "../../hooks/useRitualInvites"
import { useCancelJoinRequest, useRequestToJoinRitual } from "../../hooks/useRitualJoinRequests"
import type { RitualButtonState } from "../../pages/ritualDetail"
import {
  addRitualToCalendar,
  adminCompleteRitual,
  completeSelfGuidedRitual,
  deleteRitual,
  getCompletedDaysByMonth,
  startSelfGuidedRitual,
} from "../../services/ritual.service"
import { joinRitual, leaveRitual } from "../../services/ritual-membership.api"
import { formatRitualType } from "../../types"
import type { RitualDetailData } from "../../types/detail.types"
import { formatDuration } from "../../utils/formatDuration"
import PaymentModal from "../payment/PaymentModal"
import { DonationPopup } from "./DonationPopup"
import { ParticipantsModal } from "./ParticipantsModal"
import { RitualEarningsSection } from "./RitualEarningsSection"
import { RitualProgressSection } from "./RitualProgressSection"
import { SupportRitualBox } from "./SupportRitualBox"

function getMeetingPlatform(meetingLink: string): string | null {
  try {
    const url = new URL(meetingLink)
    const host = url.hostname.toLowerCase()
    if (host.includes("zoom.us")) return "Zoom"
    if (host.includes("meet.google.com")) return "Google Meet"
    if (host.includes("teams.microsoft.com") || host.includes("teams.live.com"))
      return "Microsoft Teams"
    if (host.includes("webex.com")) return "Webex"
    if (host.includes("gotomeeting.com")) return "GoTo Meeting"
    if (host.includes("joinme.com")) return "Join.me"
  } catch {}
  return null
}

// ============================================================================
// Action Card
// ============================================================================

interface RitualActionCardProps {
  data: RitualDetailData
  buttonState: RitualButtonState
  onActionSuccess?: () => void
  onShowRefundModal?: () => void
  onShare?: () => void
  isGuest?: boolean
}

export function RitualActionCard({
  data,
  buttonState,
  onActionSuccess,
  onShowRefundModal,
  onShare: _onShare,
  isGuest = false,
}: RitualActionCardProps) {
  const { t } = useTranslation("rituals")
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore(state => state.user?.id)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false)

  // "Add to calendar" eligibility — mirrors the backend allowlist
  // (TIME_ANCHORED_TYPES: IN_PERSON | VIRTUAL_COURSE), member or organizer.
  const canAddToCalendar =
    !isGuest &&
    (data.membershipDetails?.isMember || data.isOwner || data.isOrganizer) &&
    (data.ritualType === "IN_PERSON" || data.ritualType === "VIRTUAL_COURSE") &&
    Boolean(data.date) &&
    Boolean(data.time)

  const handleAddToCalendar = async () => {
    if (isAddingToCalendar) return
    setIsAddingToCalendar(true)
    try {
      const result = await addRitualToCalendar(data.id)
      if (result.syncStatus === "synced") {
        toast.success("Added to Google Calendar")
      } else if (result.syncStatus === "pending") {
        toast.success("Saved — will sync to Google Calendar once connected", { duration: 4000 })
        setTimeout(() => navigate("/settings?section=connected-accounts"), 1500)
      } else {
        toast.error("Failed to sync to Google Calendar. Will retry automatically.")
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } }
      if (axiosError.response?.status === 409) {
        toast.info("This ritual is already in your Google Calendar")
      } else {
        toast.error(axiosError.response?.data?.message || "Failed to add to Google Calendar")
      }
    } finally {
      setIsAddingToCalendar(false)
    }
  }
  const [isBuying, setIsBuying] = useState(false)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)
  const [isDeleteAction, setIsDeleteAction] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [completedDates, setCompletedDates] = useState<string[]>([])
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [showDonationPopup, setShowDonationPopup] = useState(false)

  const { requestToJoinAsync, isRequesting } = useRequestToJoinRitual(data.id)
  const { cancelRequestAsync, isCancelling } = useCancelJoinRequest(data.id)
  const { acceptRitualMemberInviteAsync, isAccepting } = useAcceptRitualMemberInvite(data.id)

  const fetchCompletedDates = useCallback(
    async (month: Date) => {
      try {
        const dates = await getCompletedDaysByMonth(
          data.id,
          month.getMonth() + 1,
          month.getFullYear(),
        )
        setCompletedDates(dates)
      } catch (error) {
        console.error("Failed to fetch completed days:", error)
      }
    },
    [data.id],
  )

  useEffect(() => {
    // Only fetch completed dates for self-guided courses (authenticated users only)
    if (currentUserId && data.format === "self-guided") {
      fetchCompletedDates(currentMonth)
    }
  }, [currentUserId, currentMonth, fetchCompletedDates, data.format])

  const handleAction = async () => {
    // No action for creators/organizers
    if (!buttonState.showActionButton) {
      return
    }

    // Prevent double-click on Buy button
    if (isBuying) {
      return
    }

    // Self-guided ritual/course: START RITUAL action
    const isSelfGuidedFormat = data.format === "self-guided" || data.format === "self-guided-course"
    if (buttonState.showStartButton && isSelfGuidedFormat && !buttonState.isInProgress) {
      setIsLoading(true)
      try {
        await startSelfGuidedRitual(data.id)
        toast.success(
          data.format === "self-guided-course"
            ? "Course started! Continue when you're ready."
            : "Ritual started! Complete it when you're ready.",
        )
        onActionSuccess?.()
      } catch (error) {
        console.error("Failed to start ritual:", error)
        toast.error("Failed to start ritual")
      } finally {
        setIsLoading(false)
      }
      return
    }

    // Self-guided ritual: MARK AS DONE button
    if (
      buttonState.actionLabel === "MARK AS DONE" ||
      buttonState.actionLabel === (t?.("ritualDetail.buttons.markAsDone") || "MARK AS DONE")
    ) {
      setIsLoading(true)
      try {
        await completeSelfGuidedRitual(data.id)
        toast.success("Ritual completed!")
        await fetchCompletedDates(currentMonth)
        onActionSuccess?.()
      } catch (error) {
        console.error("Failed to complete ritual:", error)
        toast.error("Failed to complete ritual")
      } finally {
        setIsLoading(false)
      }
      return
    }

    // Joined state buttons (JOINED) - no action on click
    // Leave confirmation is only shown via hamburger menu
    if (buttonState.showHamburger) {
      return
    }

    if (data.isAdminCompleted) {
      toast.info("This ritual has been completed and is no longer available")
      return
    }

    if (data.isPaid && data.fee !== "donation") {
      // Redirect to Stripe Checkout (paid rituals only, not donation-based)
      setIsBuying(true)
      setShowPaymentModal(true)
      return
    }

    // Accept invite (user was invited to this ritual — works for both public and private)
    if (buttonState.isInvited && buttonState.inviteId) {
      setIsLoading(true)
      try {
        await acceptRitualMemberInviteAsync(buttonState.inviteId)
        onActionSuccess?.()
      } catch (error) {
        console.error("Failed to accept invite:", error)
      } finally {
        setIsLoading(false)
      }
      return
    }

    // Request to join ritual with invite_req setting
    if (buttonState.isInviteReq) {
      if (buttonState.hasPendingRequest) {
        // Cancel the pending request
        setIsLoading(true)
        try {
          await cancelRequestAsync()
          onActionSuccess?.()
        } finally {
          setIsLoading(false)
        }
      } else {
        // Send join request
        setIsLoading(true)
        try {
          await requestToJoinAsync()
          onActionSuccess?.()
        } finally {
          setIsLoading(false)
        }
      }
      return
    }

    // Donation-based ritual: open donation modal
    if (data.fee === "donation") {
      setShowDonationPopup(true)
      return
    }

    // Join free ritual
    setIsLoading(true)
    try {
      await joinRitual(data.id)
      onActionSuccess?.()
    } catch (error) {
      console.error("Failed to join ritual:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePaymentError = (error: string) => {
    setPaymentError(error)
    setShowPaymentModal(false)
    setIsBuying(false)
  }

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false)
    setIsBuying(false)
    onActionSuccess?.()
  }

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false)
    setIsBuying(false)
  }

  const handleJoinWithoutDonation = async () => {
    setIsLoading(true)
    try {
      await joinRitual(data.id)
      onActionSuccess?.()
    } catch (error) {
      console.error("Failed to join ritual:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDonationSuccess = () => {
    setShowDonationPopup(false)
    onActionSuccess?.()
  }

  const handleLeave = async () => {
    if (data.isAdminCompleted) {
      toast.info("You cannot leave a completed ritual")
      return
    }
    setIsLoading(true)
    try {
      await leaveRitual(data.id)
      setShowLeaveConfirm(false)
      onActionSuccess?.()
    } catch (error) {
      console.error("Failed to leave ritual:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      await deleteRitual(data.id)
      setShowLeaveConfirm(false)
      toast.success("Ritual deleted successfully")
      // Invalidate cached teachings + stats so the deleted ritual disappears immediately
      if (currentUserId) {
        queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", currentUserId] })
        queryClient.invalidateQueries({ queryKey: ["practiceStatistics", currentUserId] })
        queryClient.invalidateQueries({ queryKey: ["suggested-rituals"] })
      }
      navigate("/my-rituality?tab=teaching")
    } catch (error) {
      console.error("Failed to delete ritual:", error)
      toast.error("Failed to delete ritual")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div
        className="rounded-[20px] md:border md:border-[#FFFFFF1A] md:bg-[#FFFFFF1A] md:p-5 space-y-5"
        id="tour-ritual-action-card"
      >
        {/* Format badges + organizer names */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap text-xs text-[#F2F0EBE6]  text-[13px] leading-5">
            <span
              className={`${data.fee === "free" ? "bg-brand-olive" : "bg-brand-terracotta"} px-1.5 py-0.5 rounded-[5px]`}
            >
              {data.fee === "free"
                ? "Free"
                : data.fee === "donation"
                  ? "Donation-based"
                  : `$${data.price}`}
            </span>
            <span>{data.ritualType ? formatRitualType(data.ritualType) : ""}</span>
          </div>
          {data.isOwner && (
            <span className={`text-[13px] font-semibold leading-5 text-[#F2F0EB]`}>
              {data.isCompleted || data.isAdminCompleted
                ? t("adminBar.completed")
                : data.canceledAt
                  ? t("adminBar.canceled")
                  : data?.isLive
                    ? t("adminBar.active")
                    : t("adminBar.draft")}
            </span>
          )}
          {/* Status label for non-admin users on canceled/completed rituals */}
          {!data.isOwner && (data.completed || data.canceled) && (
            <span
              className={`text-[13px] font-semibold leading-5 px-1.5 py-0.5 rounded-[5px] ${
                data.canceled ? "bg-brand-terracotta text-[#000]" : "bg-brand-olive text-[#000]"
              }`}
            >
              {data.canceled ? t("adminBar.canceled") : t("adminBar.completed")}
            </span>
          )}
        </div>

        {/* Organizer Actions - Edit & Menu (Delete/Complete) */}
        {data.isOwner && (
          <div className="flex gap-2 items-center w-full">
            {data.canceledAt ? (
              <Button
                variant="outline-gradient"
                className="flex-1 gap-2 py-3.75 h-12.5 text-sm w-full font-semibold uppercase !text-brand-terracotta bg-[#c867571a] hover:!bg-[#c867571a] hover:!text-brand-terracotta"
                style={{
                  border: "1px dashed rgba(200, 103, 87, 0.50)",
                  borderRadius: "100px",
                }}
              >
                {t("adminBar.canceled")}
              </Button>
            ) : (
              <Button
                variant={data.isCompleted || data.isAdminCompleted ? "primary" : "outline"}
                className={`flex-1 gap-2 border-[#F2F0EB] py-3.75 h-12.5 text-sm w-full font-semibold ${data.isCompleted || data.isAdminCompleted ? "uppercase" : "text-primary"}`}
                onClick={() => {
                  if (data.isAdminCompleted) {
                    navigate(`/rituals/create?sourceId=${data.id}`)
                  } else {
                    navigate(`/rituals/edit/${data.id}`)
                  }
                }}
              >
                {data.isCompleted || data.isAdminCompleted
                  ? t("adminBar.duplicateToRelist")
                  : t("adminBar.editRitual")}
              </Button>
            )}
            {/* Dropdown Menu with Delete and Complete options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-5.5 w-5.5 border-0 hidden md:flex">
                  <EllipsisVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {!(data.isCompleted || data.isAdminCompleted || data.canceledAt) && (
                  <DropdownMenuItem
                    className="cursor-pointer gap-2"
                    onClick={async () => {
                      try {
                        await adminCompleteRitual(data.id)
                        toast.success("Ritual completed!")
                        onActionSuccess?.()
                      } catch (error) {
                        console.error("Failed to complete ritual:", error)
                        toast.error("Failed to complete ritual")
                      }
                    }}
                  >
                    <Check className="w-4 h-4 text-brand-olive" />
                    <span>Complete</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="cursor-pointer gap-2 text-red focus:text-red"
                  onClick={() => {
                    setIsDeleteAction(true)
                    setShowLeaveConfirm(true)
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t("adminBar.delete")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Action Buttons */}
        {(buttonState.showHamburger || buttonState.showActionButton) && (
          <div className="flex gap-2.5 items-center justify-center">
            {/* Primary Action Button */}
            <div
              className="w-full"
              id={buttonState.isInProgress ? "tour-ritual-complete" : "tour-ritual-start-btn"}
            >
              {buttonState.showActionButton &&
                (isGuest ? (
                  <GuestAction>
                    <Button
                      variant={
                        buttonState.buttonVariant ||
                        (buttonState.showStartButton ? "primary" : "secondary")
                      }
                      fullWidth
                      className={`h-12.5 py-3.75 text-sm font-semibold ${
                        data.canceledAt
                          ? "text-white capitalize"
                          : buttonState.isInProgress
                            ? "bg-brand-olive text-white"
                            : buttonState.showStartButton
                              ? "bg-white text-[#000] border border-gray-200 hover:bg-gray-50"
                              : buttonState.showHamburger
                                ? "bg-brand-olive text-white hover:bg-brand-olive/90"
                                : "bg-white text-[#000] border border-gray-200 hover:bg-gray-50"
                      }`}
                      style={
                        data.canceledAt
                          ? {
                              backgroundColor: "rgba(200, 103, 87, 0.10)",
                              border: "1px dashed rgba(200, 103, 87, 0.50)",
                              borderRadius: "100px",
                              color: "rgb(200, 102, 87)",
                            }
                          : undefined
                      }
                    >
                      {data.canceledAt ? t("adminBar.canceled") : buttonState.actionLabel}
                    </Button>
                  </GuestAction>
                ) : (
                  <Button
                    variant={
                      buttonState.buttonVariant ||
                      (buttonState.showStartButton ? "primary" : "secondary")
                    }
                    fullWidth
                    onClick={data.canceledAt ? undefined : handleAction}
                    disabled={
                      (isLoading ||
                        isBuying ||
                        isRequesting ||
                        isCancelling ||
                        isAccepting ||
                        (buttonState.showStartButton && buttonState.canStartRitual === false)) &&
                      !data.canceledAt
                    }
                    title={
                      buttonState.showStartButton && buttonState.canStartRitual === false
                        ? t?.("ritualDetail.messages.minParticipantsRequired") ||
                          "Minimum participants required"
                        : undefined
                    }
                    className={`h-12.5 py-3.75 text-sm font-semibold ${
                      data.canceledAt
                        ? "text-white capitalize"
                        : buttonState.isInProgress
                          ? "bg-brand-olive text-white"
                          : buttonState.showStartButton
                            ? "bg-white text-[#000] border border-gray-200 hover:bg-gray-50"
                            : buttonState.showHamburger
                              ? "bg-brand-olive text-white hover:bg-brand-olive/90"
                              : "bg-white text-[#000] border border-gray-200 hover:bg-gray-50"
                    }`}
                    style={
                      data.canceledAt
                        ? {
                            backgroundColor: "rgba(200, 103, 87, 0.10)",
                            border: "1px dashed rgba(200, 103, 87, 0.50)",
                            borderRadius: "100px",
                            color: "rgb(200, 102, 87)",
                          }
                        : undefined
                    }
                  >
                    {isLoading || isBuying
                      ? "Processing..."
                      : data.canceledAt
                        ? t("adminBar.canceled")
                        : buttonState.actionLabel}
                  </Button>
                ))}
            </div>

            {/* Hamburger Menu Button - shown for all joined members (not just free) */}
            {buttonState.showHamburger && !data.isAdminCompleted && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden md:flex h-5.5 w-5.5 p-0 flex-shrink-0"
                  >
                    <EllipsisVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer gap-2 text-red focus:text-red"
                    onClick={() => {
                      setIsDeleteAction(false)
                      setShowLeaveConfirm(true)
                    }}
                  >
                    Leave
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {/* Cancellation Refund Info Box - shown for members when ritual is canceled and they have a paid booking */}
        {data.canceledAt && !data.isOwner && data.isPaid && data.membershipDetails?.isMember && (
          <div className="mt-3 rounded-lg border border-[#c9a8761a] bg-[#c9a8761a] py-4 px-3 space-y-3">
            <div className="text-[14px] font-medium text-white capitalize">
              {data.canceledBy
                ? "Cancelled – Admin canceled the ritual"
                : "Cancelled – minimum participants not reached"}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm  text-white ">
                {t("ritualSidebar.joinedOf", {
                  joined: data.participantStats?.joined || 0,
                  min: data.minParticipants || 0,
                })}
              </span>
              {data.refundRequest?.hasRequest ? (
                <span className="text-sm font-semibold text-brand-terracotta">Requested</span>
              ) : data.refundRequest?.status !== "REFUNDED" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-0 text-[14px] font-semibold text-brand-terracotta underline"
                  onClick={onShowRefundModal}
                >
                  Request Refund
                </Button>
              ) : (
                <span className="text-sm font-semibold text-brand-terracotta">Refunded</span>
              )}
            </div>
          </div>
        )}

        {/* Leave/Delete Confirmation Modal */}
        <LeaveRitualModal
          isOpen={showLeaveConfirm}
          onClose={() => {
            setShowLeaveConfirm(false)
            setIsDeleteAction(false)
          }}
          onConfirm={isDeleteAction ? handleDelete : handleLeave}
          isLoading={isLoading}
          ritualTitle={data.title}
          isDelete={isDeleteAction}
        />

        {/* Meta information */}
        <div className="space-y-2.5">
          {/* Duration / Date / Time */}
          <div className="flex items-center gap-2.5">
            <Calendar className="w-6 h-6 text-stone flex-shrink-0" />
            <div className="text-sm leading-6 text-primary">
              {data.date &&
              data.time &&
              data.ritualType !== "SELF_GUIDED" &&
              data.ritualType !== "SELF_GUIDED_COURSE" ? (
                <span>
                  {formatDuration(data.duration)} -{" "}
                  <span className="underline">
                    {data.date.replace(/,\s*\d{4}$/, "")}, {data.time}
                  </span>
                </span>
              ) : (
                <span>{formatDuration(data.duration)}</span>
              )}
            </div>
          </div>

          {/* Platform (virtual) */}
          {data.meetingLink && (
            <div className="flex items-center gap-2.5">
              <Monitor className="w-6 h-6 text-stone flex-shrink-0" />
              <span className="text-sm leading-6 text-primary">
                {t("ritualSidebar.platform", {
                  platform: getMeetingPlatform(data.meetingLink) || "Online",
                })}
              </span>
            </div>
          )}

          {/* Meeting Link (virtual courses) - locked for non-members */}
          {data.meetingLink && (
            <div className="flex items-center gap-2.5">
              <Video className="w-6 h-6 text-stone flex-shrink-0" />
              {buttonState.canAccessContent ? (
                <div className="flex flex-col">
                  <a
                    href={data.meetingLink}
                    className="text-sm leading-6 text-primary underline hover:text-stone transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getMeetingPlatform(data.meetingLink) || t("ritualSidebar.joinMeeting")}
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-sm leading-6 text-stone">
                  <Video className="w-3 h-3" />
                  <span>{t("ritualSidebar.joinForLink")}</span>
                </div>
              )}
            </div>
          )}

          {/* Location (in-person) - locked for non-members */}
          {data.location && (
            <div className="flex items-center gap-2.5">
              <MapPin className="w-6 h-6 text-stone flex-shrink-0" />
              {buttonState.canAccessContent ? (
                <span className="text-sm leading-6 text-primary">{data.location}</span>
              ) : (
                <span className="text-sm leading-6 text-stone">
                  {t("ritualSidebar.joinForLocation")}
                </span>
              )}
            </div>
          )}

          {/* Website Link - Hide for PERSONAL visibility */}
          {data.visibility !== "PERSONAL" && data.websiteLink && (
            <div className="flex items-center gap-2.5">
              <Link2 className="w-6 h-6 text-stone flex-shrink-0" />
              <a
                href={data.websiteLink}
                className="text-sm leading-6 text-primary underline hover:text-stone transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit website
              </a>
            </div>
          )}
          {/* Access Type */}
          {data.accessType && (
            <div className="flex items-center gap-2.5">
              <img
                src={`${import.meta.env.VITE_ASSET_BASE_URL}shield_icon.svg`}
                alt=""
                className="w-6 h-6 flex-shrink-0"
              />
              <span className="text-sm leading-6 text-primary">
                {data.accessType.toLowerCase() === "public" && "Public: Everyone can join"}
                {data.accessType.toLowerCase() === "private" && "Private: Only invited members"}
                {data.accessType.toLowerCase() === "personal" && "Personal: Only you"}
              </span>
            </div>
          )}
        </div>

        {canAddToCalendar && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAddToCalendar}
            disabled={isAddingToCalendar}
            className="w-full min-h-[50px] rounded-full border border-white text-white text-[14px] font-semibold uppercase flex items-center justify-center gap-2.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
          >
            <Calendar className="w-5 h-5" />
            <span>{isAddingToCalendar ? "Adding..." : "Add to Calendar"}</span>
          </Button>
        )}

        {data.format === "self-guided" &&
          !data.isOwner &&
          !data.isOrganizer &&
          !(data.metadata as Record<string, unknown> | null)?.isOnboardingDemo && (
            <>
              <div className="bg-[#D9D9D91A]" />

              {/* Ritual Attendance Calendar */}
              <div>
                <MonthViewCalendar
                  currentMonth={currentMonth}
                  maxMonth={new Date()}
                  selectedDates={completedDates.map(dateStr => {
                    const [year, month, day] = dateStr.split("-").map(Number)
                    return new Date(year, month - 1, day)
                  })}
                  onMonthChange={setCurrentMonth}
                  readonly
                  className="rounded-lg bg-brand-white-alpha-5"
                />
              </div>
            </>
          )}
      </div>

      {/* Payment Modal - redirects to Stripe Checkout */}
      {showPaymentModal && (
        <PaymentModal
          ritual={data}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={handlePaymentModalClose}
        />
      )}

      {/* Donation Popup - for donation-based rituals */}
      <DonationPopup
        open={showDonationPopup}
        onOpenChange={setShowDonationPopup}
        ritualId={data.id}
        ritualName={data.title}
        onDonateSuccess={handleDonationSuccess}
        onJoinWithoutDonation={handleJoinWithoutDonation}
      />

      {/* Payment Error */}
      {paymentError && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md mx-4">
            <p className="text-red-600 mb-4">{paymentError}</p>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  setPaymentError(null)
                  setShowPaymentModal(true)
                }}
              >
                Try Again
              </Button>
              <Button variant="outline" onClick={() => setPaymentError(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================================
// Community / Participants Activity
// ============================================================================

interface ActivitySectionProps {
  data: RitualDetailData
  isGuest?: boolean
}

// ============================================================================
// Invite Friends Dialog
// ============================================================================

interface InviteFriendsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ritualId: string
}

function InviteFriendsDialog({ open, onOpenChange, ritualId }: InviteFriendsDialogProps) {
  const { t } = useTranslation("rituals")
  const { data: invitees, isLoading } = useRitualMemberInvitees(ritualId)
  const { inviteRitualMember, isInviting } = useInviteRitualMember(ritualId)
  const { resendRitualMemberInvite, isResending } = useResendRitualMemberInvite(ritualId)
  const { withdrawRitualMemberInvite, isWithdrawing } = useWithdrawRitualMemberInvite(ritualId)

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "JOINED":
        return (
          <span className="flex items-center gap-1.5 text-xs">
            <InviteAcceptedIcon size={14} />
            Joined
          </span>
        )
      case "INVITED":
        return (
          <span className="flex items-center gap-1.5 text-xs">
            <img
              src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
              alt=""
              className="w-3.5 h-3.5"
            />
            Invited
          </span>
        )
      case "DECLINED":
        return (
          <span className="flex items-center gap-1.5 text-xs">
            <InviteDeclinedIcon size={14} />
            Declined
          </span>
        )
      default:
        return null
    }
  }

  const joinedCount = invitees?.filter(i => i.status === "JOINED").length || 0
  const invitedCount = invitees?.filter(i => i.status === "INVITED").length || 0

  const handleInvite = (userId: string) => {
    inviteRitualMember(userId)
  }

  const handleResend = (inviteId: string) => {
    resendRitualMemberInvite(inviteId)
  }

  const handleWithdraw = (inviteId: string) => {
    withdrawRitualMemberInvite(inviteId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden gap-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-olive/10 to-brand-beige/30 px-6 py-5 border-b border-color-brand-white-alpha-10">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold text-primary mb-1">
                {t("adminBar.inviteFriends")}
              </DialogTitle>
              <p className="text-sm text-stone">
                {joinedCount} joined · {invitedCount} invited
              </p>
            </div>
          </div>
        </div>

        {/* Invited Friends List */}
        <div className="max-h-[300px] overflow-y-auto">
          <div className="px-6 py-3 bg-brand-white-alpha-5">
            <p className="text-xs font-medium text-stone uppercase tracking-wide">
              {isLoading ? "Loading..." : "Members"}
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-olive" />
            </div>
          ) : (
            <div className="divide-y divide-color-brand-white-alpha-10">
              {invitees?.map(invitee => (
                <div
                  key={invitee.userId}
                  className="flex items-center gap-3 px-6 py-3 hover:bg-brand-white-alpha-5 transition-colors"
                >
                  <Avatar
                    src={invitee.user?.profilePictureUrl ?? undefined}
                    fallback={invitee.user?.name || invitee.email || "?"}
                    size="md"
                    className="w-10 h-10"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">
                      {invitee.user?.name || invitee.email}
                    </p>
                    {invitee.user?.username && (
                      <p className="text-xs text-stone truncate">{invitee.user.username}</p>
                    )}
                  </div>
                  {getStatusBadge(invitee.status)}
                  {invitee.status === "not_invited" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-5 text-xs"
                      onClick={() => handleInvite(invitee.userId)}
                      disabled={isInviting}
                    >
                      Invite
                    </Button>
                  )}
                  {(invitee.status === "INVITED" || invitee.status === "DECLINED") && invitee.id ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                          <MoreVertical size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            if (invitee.id) handleResend(invitee.id)
                          }}
                          disabled={isResending}
                          className="cursor-pointer"
                        >
                          <RefreshCw size={16} className="mr-2" />
                          Resend
                        </DropdownMenuItem>
                        {invitee.status === "INVITED" && (
                          <DropdownMenuItem
                            onClick={() => {
                              if (invitee.id) handleWithdraw(invitee.id)
                            }}
                            disabled={isWithdrawing}
                            className="text-red focus:text-red cursor-pointer"
                          >
                            <Trash2 size={16} className="mr-2" />
                            Withdraw
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              ))}
              {(!invitees || invitees.length === 0) && (
                <div className="px-6 py-8 text-center text-sm text-stone">
                  No connections found. Follow users to invite them!
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ActivitySection({ data, isGuest = false }: ActivitySectionProps) {
  const { t } = useTranslation("rituals")
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [showParticipantsModal, setShowParticipantsModal] = useState(false)
  const currentUserId = useAuthStore(state => state.user?.id)

  // Can the current user view the participants list?
  // Uses whoCanSeeParticipantList privacy setting (owner/organizers/participants/everyone).
  // Falls back to accessType check for legacy rituals without the setting.
  const participantListSetting = data.whoCanSeeParticipantList
  const canViewParticipants = participantListSetting
    ? participantListSetting.includes("everyone") ||
      (participantListSetting.includes("owner") && data.isOwner) ||
      (participantListSetting.includes("organizers") && data.isOrganizer) ||
      (participantListSetting.includes("participants") && data.membershipDetails?.isMember)
    : // Legacy: no whoCanSeeParticipantList → fall back to accessType + membership
      data.accessType === "PUBLIC" ||
      data.isOwner ||
      data.isOrganizer ||
      data.membershipDetails?.isMember

  return (
    <div className="rounded-[20px] p-5 space-y-5 border border-[#FFFFFF1A] bg-[#FFFFFF1A]">
      {/* Clickable participants section */}
      <div
        className={`flex items-start justify-between ${
          isGuest
            ? ""
            : canViewParticipants
              ? "cursor-pointer hover:opacity-80 transition-opacity"
              : ""
        }`}
        onClick={() => {
          if (!isGuest && canViewParticipants) {
            setShowParticipantsModal(true)
          }
        }}
      >
        <div className="flex flex-col">
          <h3 className="text-[18px] font-semibold leading-7.5 text-primary uppercase">
            {t("ritualSidebar.participants")}
          </h3>
          {!data.isOrganizer && (
            <p className="text-xs leading-5 text-[#F2F0EBE6] -mt-1">
              {t("ritualSidebar.joinedSaved", {
                joined: data.participantStats?.joined ?? 0,
                saved: data.participantStats?.saved ?? 0,
              })}
            </p>
          )}
        </div>

        {/* Avatar stack */}
        <div className="flex items-center gap-2.5">
          {data.sampleMembers && data.sampleMembers.length > 0 && (
            <>
              {isGuest || !canViewParticipants ? (
                <GuestAction>
                  <div className="flex items-center -space-x-2">
                    {data.sampleMembers.slice(0, 3).map((member, i) => (
                      <Avatar
                        key={i}
                        src={member.profileCoverImage ?? undefined}
                        fallback={member.name || "?"}
                        size="lg"
                        className={`w-10 h-10 bg-brand-cream-light ring-2 ring-white/20 ${!canViewParticipants ? "opacity-50" : ""}`}
                      />
                    ))}
                  </div>
                </GuestAction>
              ) : (
                <div className="flex items-center -space-x-2">
                  {data.sampleMembers.slice(0, 3).map((member, i) => (
                    <Avatar
                      key={i}
                      src={member.profileCoverImage ?? undefined}
                      fallback={member.name || "?"}
                      size="lg"
                      className="w-10 h-10 bg-brand-cream-light ring-2 ring-white/20"
                    />
                  ))}
                </div>
              )}
              {data.sampleMembers.length > 3 && (
                <div className="text-primary text-[13px] text-[#F2F0EB] leading-5.5">
                  +
                  {(data.metadata as Record<string, unknown> | null)?.isOnboardingDemo
                    ? 46
                    : data.sampleMembers.length - 3}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invite Friends — non-interactive for onboarding demo rituals */}
      {!!(data.metadata as Record<string, unknown> | null)?.isOnboardingDemo && (
        <Button
          variant="outline"
          fullWidth
          size="sm"
          className="text-sm font-semibold uppercase py-3.75 h-12.5 pointer-events-none"
        >
          {t("adminBar.inviteFriends")}
        </Button>
      )}

      {data.isOwner && (
        <div>
          <div className="h-px bg-[#F2F0EB33] mb-3.75" />
          <div className="grid grid-cols-4">
            <div className="flex flex-col gap-1">
              <span className="text-[16px] font-semibold leading-6.25 text-[#F2F0EB]">
                {data.participantStats?.views ?? 0}
              </span>
              <span className="text-[#F2F0EB80] text-sm leading-6.25">Views</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[16px] font-semibold leading-6.25">
                {data.participantStats?.joined ?? 0}
              </span>
              <span className="text-[#F2F0EB80] text-sm leading-6.25">Joined</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[16px] font-semibold leading-6.25">
                {data.participantStats?.saved ?? 0}
              </span>
              <span className="text-[#F2F0EB80] text-sm leading-6.25">Saved</span>
            </div>
            {(data.ritualType === "SELF_GUIDED" || data.ritualType === "SELF_GUIDED_COURSE") && (
              <div className="flex flex-col gap-1">
                <span className="text-[16px] font-semibold leading-6.25">
                  {data.participantStats?.completed ?? 0}
                </span>
                <span className="text-[#F2F0EB80] text-sm leading-6.25">Completed</span>
              </div>
            )}
          </div>
        </div>
      )}

      {data.canInvite && data.accessType !== "PERSONAL" && (
        <Button
          variant="outline"
          fullWidth
          size="sm"
          className="text-sm font-semibold uppercase py-3.75 h-12.5"
          onClick={() => setShowInviteDialog(true)}
        >
          {t("adminBar.inviteFriends")}
        </Button>
      )}

      <InviteFriendsDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        ritualId={data.id}
      />

      <ParticipantsModal
        open={showParticipantsModal}
        onOpenChange={setShowParticipantsModal}
        ritualId={data.id}
        currentUserId={currentUserId}
      />
    </div>
  )
}

// ============================================================================
// Organizer Section
// ============================================================================

interface OrganizerSectionProps {
  organizers: RitualDetailData["organizers"]
  label?: string
  ritualId?: string
  isOrganizer?: boolean
  createdBy?: string | null
  totalOrganizers?: number
  isGuest?: boolean
}

function formatFollowers(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1).replace(/\.0$/, "")}M`
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`
  }
  return String(count)
}

export function OrganizerSection({
  organizers,
  label,
  ritualId,
  isOrganizer,
  createdBy,
  totalOrganizers,
  isGuest = false,
}: OrganizerSectionProps) {
  const { t } = useTranslation("rituals")
  const currentUserId = useAuthStore(state => state.user?.id)
  const sectionTitle =
    label ||
    (organizers.length === 1 ? t("ritualSidebar.organizer") : t("ritualSidebar.organizers"))
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: existingGroupChat } = useOrganizerChatByRitualId(isOrganizer ? ritualId : undefined)
  const createGroupChat = useCreateRitualGroupChat()
  const createDmConversation = useCreateDmConversation()
  const { followAsync, unfollowAsync, isFollowing, isUnfollowing } = useFollowActions()
  const {
    join: joinCommunity,
    leave: leaveCommunity,
    isJoining,
    isLeaving,
  } = useCommunityJoinActions()

  const handleChatWithOrganizers = async () => {
    if (existingGroupChat) {
      navigate(`/chats/ritual/${existingGroupChat.id}`)
      return
    }
    if (!ritualId) return
    try {
      const chat = await createGroupChat.mutateAsync(ritualId)
      navigate(`/chats/ritual/${chat.id}`)
    } catch {
      // Error handled by React Query
    }
  }

  const handleMessage = async (org: (typeof organizers)[number]) => {
    if (org.organiserType === "community" && org.community?.id) {
      navigate(`/chats?community=${org.community.id}`)
    } else if (org.organiserType === "user" && org.user?.id) {
      try {
        const conversation = await createDmConversation.mutateAsync(org.user.id)
        navigate(`/chats/dm/${conversation.id}`)
      } catch {}
    }
  }

  const handleFollowToggle = async (org: (typeof organizers)[number]) => {
    try {
      if (org.organiserType === "community" && org.community?.id) {
        if (org.community.isMember) {
          await leaveCommunity(org.community.id)
        } else {
          await joinCommunity(org.community.id)
        }
      } else if (org.organiserType === "user" && org.user?.id) {
        if (org.user.isFollowing) {
          await unfollowAsync(org.user.id)
        } else {
          await followAsync(org.user.id)
        }
      }
      if (ritualId) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] }),
          queryClient.invalidateQueries({ queryKey: ["ritual-organizers", ritualId] }),
        ])
      }
    } catch {}
  }

  return (
    <div className="rounded-[20px] border border-[#FFFFFF1A] bg-[#FFFFFF1A] p-5 space-y-3">
      <div className="text-[18px] leading-7.5 font-semibold text-primary uppercase flex items-center">
        {sectionTitle}
        <span className="ml-1.25 text-[#F2F0EB66] text-[15px] leading-5.5 font-normal">
          {organizers.length}
        </span>
      </div>

      <div className="space-y-3">
        {organizers.map(org => (
          <div key={org.id} className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (org.organiserType === "community" && org.community?.id) {
                  navigate(`/communities/${org.community.id}`)
                } else if (org.organiserType === "user" && org.user?.id) {
                  navigate(`/profile/${org.user.id}`)
                }
              }}
              className="flex items-center gap-[15px] flex-1 min-w-0 text-left hover:bg-brand-beige/30 rounded-lg p-1 -ml-1 transition-colors cursor-pointer"
            >
              <Avatar
                src={
                  org.user?.coverImage ||
                  org.community?.coverImage ||
                  org.user?.profilePictureUrl ||
                  org.avatar
                }
                fallback={org.name}
                size="lg"
                className={`bg-beige-medium w-13.75 h-13.75 ${org.organiserType === "community" ? "rounded-[10px]" : "rounded-full"}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-primary truncate">{org.name}</p>
                <p className="text-xs leading-5 text-stone">
                  {org.organiserType === "community"
                    ? `${formatFollowers(org.community?.totalMemberCount ?? 0)} members`
                    : `${formatFollowers(org.user?.followerCount ?? org.followers ?? 0)} followers`}
                </p>
              </div>
            </button>
            {/* Hide message/follow buttons for the creator's own organizer card or guests */}
            {!isGuest && !(currentUserId === createdBy) && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  className="text-primary hover:text-stone transition-colors disabled:opacity-50"
                  aria-label={`Message ${org.name}`}
                  onClick={e => {
                    e.stopPropagation()
                    handleMessage(org)
                  }}
                  disabled={
                    isFollowing ||
                    isUnfollowing ||
                    isJoining ||
                    isLeaving ||
                    createDmConversation.isPending
                  }
                >
                  <MessageCircle className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  className="text-primary hover:text-stone transition-colors disabled:opacity-50"
                  aria-label={
                    org.organiserType === "community"
                      ? org.community?.isMember
                        ? `Leave ${org.name}`
                        : `Join ${org.name}`
                      : org.user?.isFollowing
                        ? `Unfollow ${org.name}`
                        : `Follow ${org.name}`
                  }
                  onClick={e => {
                    e.stopPropagation()
                    handleFollowToggle(org)
                  }}
                  disabled={isFollowing || isUnfollowing || isJoining || isLeaving}
                >
                  {org.organiserType === "community" ? (
                    <img
                      src={
                        org.community?.isMember
                          ? getAssetUrl("add_community.svg")
                          : getAssetUrl("organiser_team.svg")
                      }
                      alt=""
                      className="w-5 h-5"
                    />
                  ) : org.user?.isFollowing ? (
                    <HeartFilledIcon width={21} height={21} color="#8d8e63" />
                  ) : (
                    <HeartOutlineIcon width={21} height={21} color="#F2F0EB" />
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {isOrganizer && (totalOrganizers ?? 0) > 1 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 py-6 uppercase font-semibold"
          onClick={handleChatWithOrganizers}
          disabled={createGroupChat.isPending}
        >
          Chat with Organizers
        </Button>
      )}
    </div>
  )
}

// ============================================================================
// Share Section
// ============================================================================

interface ShareSectionProps {
  ritualId: string
  ritualSlug?: string
  ritualTitle: string
  ritualImageUrl?: string | null
  onShare?: () => void
}

export function ShareSection({
  ritualId,
  ritualSlug,
  ritualTitle,
  ritualImageUrl = null,
  onShare,
}: ShareSectionProps) {
  const { t } = useTranslation("rituals")
  const [_copied, setCopied] = useState(false)

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/rituals/${ritualSlug || ritualId}`
      : ""
  const shareText = `Check out this ritual: ${ritualTitle}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success("Link copied to clipboard!")
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error("Failed to copy link:", error)
      toast.error("Failed to copy link. Please try again.")
    }
  }

  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const { isConnected: isInstagramConnected } = useInstagramConnection()

  const handleInstagramClick = () => {
    if (isInstagramConnected) {
      setShowPublishDialog(true)
    }
  }

  const socialPlatforms = [
    {
      name: "Instagram",
      icon: getAssetUrl("instagram-web.png"),
      action: handleInstagramClick,
    },
    {
      name: "Facebook",
      icon: getAssetUrl("facebook-web.png"),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    },
    {
      name: "TikTok",
      icon: getAssetUrl("tiktok-web.png"),
      url: `https://www.tiktok.com/search?q=${encodeURIComponent(shareText)}`,
    },
    {
      name: "Twitter",
      icon: getAssetUrl("twitter-web.png"),
      url: `https://www.twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "Thread",
      icon: getAssetUrl("threads-web.png"),
      url: `https://www.threads.net/intent/post?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    },
    {
      name: "Link",
      icon: getAssetUrl("link-web.png"),
      action: handleCopyLink,
    },
    {
      name: "More",
      icon: getAssetUrl("more-web.png"),
      action: onShare,
    },
  ]

  const handleShare = async (platform: (typeof socialPlatforms)[0]) => {
    if (platform.action) {
      await platform.action()
    } else if (platform.url) {
      window.open(platform.url, "_blank", "width=600,height=400")
    }
  }

  return (
    <>
      <div className="rounded-[20px] border border-[#FFFFFF1A] bg-[#FFFFFF1A] p-5 space-y-2.5">
        <div className="text-[18px] leading-7.5 font-semibold text-primary uppercase">
          {t("adminBar.shareYourRitual")}
        </div>

        <div className="flex items-start justify-between gap-2.5">
          {socialPlatforms.map(platform => {
            return (
              <button
                key={platform.name}
                type="button"
                onClick={() => handleShare(platform)}
                className="cursor-pointer w-12 h-12 lg:w-8 lg:h-8 xl:w-10 xl:h-10"
              >
                {typeof platform.icon === "string" ? (
                  <img
                    src={platform.icon}
                    alt={platform.name}
                    className="w-full h-full rounded-full"
                  />
                ) : (
                  <Link2 />
                )}
              </button>
            )
          })}
        </div>

        {!isInstagramConnected && (
          <div className="mt-2">
            <ConnectInstagramPrompt />
          </div>
        )}
      </div>

      <InstagramShareDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        shareContext={{
          type: "ritual",
          ritualId,
          title: ritualTitle,
          description: "",
          imageUrl: ritualImageUrl,
        }}
      />
    </>
  )
}

// ============================================================================
// Rituality Coach Widget
// ============================================================================

export function RitualCoachWidget({
  ritualId,
  isGuest = false,
}: {
  ritualId: string
  isGuest?: boolean
}) {
  const { t } = useTranslation("rituals")
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)

  const handleChat = async () => {
    if (isStarting) return
    setIsStarting(true)
    try {
      // Backend creates the conversation and persists the greeting + prep
      // assistant messages so they survive logout/refresh.
      const { id } = await startCoachChat(ritualId)
      navigate(`/chats/ai/${id}`)
    } catch {
      toast.error("Failed to start Rituality Coach chat")
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div
      id="tour-ritual-coach"
      className="rounded-[20px] p-5 flex items-center justify-between gap-3 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${getAssetUrl("chat_banner.png")}")` }}
    >
      <div className="text-sm font-semibold text-stone">
        {t("ritualSidebar.questionsAboutRitual")}
        <br />
        {t("ritualSidebar.askYourCoach")}
      </div>
      {isGuest ? (
        <GuestAction>
          <Button variant="primary" size="sm" className="gap-2 flex-shrink-0">
            <Sparkles className="w-4 h-4" />
            Chat
          </Button>
        </GuestAction>
      ) : (
        <Button
          variant="primary"
          size="sm"
          className="gap-2 flex-shrink-0"
          onClick={handleChat}
          disabled={isStarting}
        >
          <Sparkles className="w-4 h-4" />
          {isStarting ? "Starting..." : "Chat"}
        </Button>
      )}
    </div>
  )
}

// ============================================================================
// Complete Sidebar
// ============================================================================

interface RitualSidebarProps {
  data: RitualDetailData
  buttonState: RitualButtonState
  onActionSuccess?: () => void
  onShare?: () => void
  organizers?: RitualDetailData["organizers"]
  totalOrganizers?: number
  isGuest?: boolean
}

export function RitualSidebar({
  data,
  buttonState,
  onActionSuccess,
  onShare: _onShare,
  organizers,
  totalOrganizers,
  isGuest = false,
}: RitualSidebarProps) {
  const [showRefundModal, setShowRefundModal] = useState(false)
  const isMobile = useIsMobile()
  return (
    <div className="space-y-5 lg:top-[76px]">
      {!isMobile && (
        <RitualActionCard
          data={data}
          buttonState={buttonState}
          onActionSuccess={onActionSuccess}
          onShowRefundModal={() => setShowRefundModal(true)}
          onShare={_onShare}
          isGuest={isGuest}
        />
      )}
      <RitualEarningsSection data={data} />
      {!data.isOwner && data.isDonationBased && (
        <SupportRitualBox
          ritualId={data.id}
          ritualName={data.title}
          userDonationAmount={data.userDonationAmount ?? null}
          onDonateSuccess={onActionSuccess}
          isGuest={isGuest}
        />
      )}

      {/* Course Progress Section */}
      <RitualProgressSection
        courseProgress={data.courseProgress}
        isOwner={data.isOwner}
        isOrganizer={data.isOrganizer}
      />

      <ActivitySection data={data} isGuest={isGuest} />
      <OrganizerSection
        organizers={organizers || data.organizers}
        label={data.organizerLabel}
        ritualId={data.id}
        isOrganizer={data.isOrganizer}
        createdBy={data.createdBy}
        totalOrganizers={totalOrganizers}
        isGuest={isGuest}
      />
      {data?.isOwner && !isMobile && (
        <ShareSection
          ritualId={data.id}
          ritualSlug={data.slug}
          ritualTitle={data.title}
          ritualImageUrl={
            data.media?.find(m => m.isCover)?.url ||
            data.media?.find(m => m.type === "image")?.url ||
            null
          }
          onShare={_onShare}
        />
      )}
      <RitualCoachWidget ritualId={data.id} isGuest={isGuest} />

      {/* Refund Request Modal */}
      {data.canceledAt && (
        <RefundRequestModal
          isOpen={showRefundModal}
          onOpenChange={setShowRefundModal}
          ritualTitle={data.title}
          amount={`$${data.price ?? 0}`}
          onConfirm={async () => {
            const { submitRefundRequest } = await import(
              "@/features/booking/services/booking.service"
            )
            await submitRefundRequest(data.id)
            onActionSuccess?.()
          }}
        />
      )}
    </div>
  )
}
